/* eslint-disable prettier/prettier */
import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/Button';
import { RoleService } from '../../services/role.service';
import { RoleType } from '../../types/RoleType';
import { Edit, Plus, Trash2 } from 'lucide-react';
import { RoleModal } from '../../components/role/RoleModal';
import { ConfirmDeleteModal } from '../../components/file/ConfirmDeleteModal';

export const RolesPage = () => {
  const [roles, setRoles] = useState<RoleType[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleType | null>(null);
  const [deletingRole, setDeletingRole] = useState<RoleType | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const formatRoleName = (roleName: string) => {
    if (!roleName) return '';
    return roleName
      .trim()
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const loadRoles = async () => {
    try {
      setIsLoading(true);
      const res = await RoleService.getRoles();
      setRoles(res.data);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!deletingRole) return;
    try {
      await RoleService.delete(deletingRole.id);
      setDeletingRole(null);
      loadRoles();
    } catch (err) {
      console.error('[Role Delete Error]', err);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  return (
    <div>
      <h1 className="text-4xl font-bold text-center sm:pb-4 sm:text-left text-primary">Roles</h1>
      <div className="flex justify-center sm:justify-start">
        <Button
          icon={<Plus />}
          className="px-16 py-3 mt-4 sm:px-6 sm:mt-0"
          onClick={() => setModalOpen(true)}
        >
          New
        </Button>
      </div>
      <div className="mt-8">
        {isLoading ? (
          <p className="mt-6 text-xl text-center text-muted-foreground text-primary">Loading...</p>
        ) : roles.length === 0 ? (
          <p className="mt-6 text-xl text-center text-primary text-muted-foreground">
            No roles found.
          </p>
        ) : (
          <div className="space-y-3">
            {roles.map((role) => (
              <div key={role.id} className="p-4 bg-white border rounded shadow-sm w-[90%]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-primary">{formatRoleName(role.name)}</p>
                    <p className="text-sm text-muted-foreground">{role.description}</p>
                  </div>
                  {role.editable && (
                    <div className="flex items-center gap-3">
                      <Edit
                        onClick={() => {
                          setEditingRole(role);
                          setModalOpen(true);
                        }}
                        className="cursor-pointer text-primary"
                      />
                      <Trash2
                        onClick={() => setDeletingRole(role)}
                        className="text-red-500 cursor-pointer hover:text-red-600"
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        <RoleModal
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setEditingRole(null);
          }}
          editingRole={editingRole}
          onSuccess={loadRoles}
        />
        <ConfirmDeleteModal
          isOpen={!!deletingRole}
          names={deletingRole ? [formatRoleName(deletingRole.name)] : []}
          onCancel={() => setDeletingRole(null)}
          onConfirm={handleDeleteRole}
        />
      </div>
    </div>
  );
};
